import React, { memo, useMemo } from "react";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import { useTranslation } from "react-i18next";
import colours from "../../theme/colours";
import { getPrimaryColour } from "../../utils/getPrimaryColour";

ChartJS.register(...registerables);

function formatR2(value) {
  return typeof value === "number" && Number.isFinite(value)
    ? value.toFixed(2)
    : null;
}

const PostExertionDecayChart = memo(function PostExertionDecayChart({
  points,
  expR2,
  linearR2,
  height = 260,
  showFits = true,
}) {
  const { t } = useTranslation();

  const data = useMemo(() => {
    const expR2Label = formatR2(expR2);
    const linearR2Label = formatR2(linearR2);
    const hasExpSeries = showFits && (points || []).some((point) => {
      const value = point?.exp;
      return typeof value === "number" && Number.isFinite(value);
    });
    const hasLinearSeries = showFits && (points || []).some((point) => {
      const value = point?.linear;
      return typeof value === "number" && Number.isFinite(value);
    });

    const datasets = [
      {
        label: t("result.postExertion.decayObservedLabel", "Observed RR"),
        data: (points || []).map((point) => ({ x: point.t, y: point.observed })),
        parsing: false,
        showLine: false,
        pointRadius: 3,
        pointHoverRadius: 4,
        borderColor: getPrimaryColour(),
        backgroundColor: getPrimaryColour({ format: "rgba", opacity: 0.8 }),
      },
    ];

    if (hasExpSeries) {
      datasets.push({
          label: expR2Label
            ? t("result.postExertion.decayExpFitLabelWithR2", {
                defaultValue: "Exp fit (R2 {{r2}})",
                r2: expR2Label,
              })
            : t("result.postExertion.decayExpFitLabel", "Exp fit"),
          data: (points || []).map((point) => ({ x: point.t, y: point.exp })),
          parsing: false,
          borderColor: colours.tertiary,
          backgroundColor: "transparent",
          pointRadius: 0,
          pointHoverRadius: 0,
          borderWidth: 2,
          tension: 0.2,
      });
    }

    if (hasLinearSeries) {
      datasets.push({
          label: linearR2Label
            ? t("result.postExertion.decayLinearFitLabelWithR2", {
                defaultValue: "Linear fit (R2 {{r2}})",
                r2: linearR2Label,
              })
            : t("result.postExertion.decayLinearFitLabel", "Linear fit"),
          data: (points || []).map((point) => ({ x: point.t, y: point.linear })),
          parsing: false,
          borderColor: colours.secondary,
          backgroundColor: "transparent",
          pointRadius: 0,
          pointHoverRadius: 0,
          borderWidth: 2,
          tension: 0,
          borderDash: [8, 6],
      });
    }

    return {
      datasets,
    };
  }, [points, expR2, linearR2, showFits, t]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "nearest",
        intersect: false,
      },
      plugins: {
        legend: {
          display: data.datasets.length > 1,
          labels: {
            color: colours.typography,
            usePointStyle: true,
            boxWidth: 10,
          },
        },
        tooltip: {
          callbacks: {
            title(items) {
              const x = items?.[0]?.parsed?.x;
              return typeof x === "number" && Number.isFinite(x)
                ? t("result.postExertion.decayTooltipTitle", {
                    defaultValue: "{{seconds}} s",
                    seconds: x.toFixed(1),
                  })
                : "";
            },
            label(item) {
              const label = item?.dataset?.label || "";
              const y = item?.parsed?.y;
              const formatted =
                typeof y === "number" && Number.isFinite(y)
                  ? y.toFixed(2)
                  : "--";
              return `${label}: ${formatted} ${t("result.resp.rrUnit", "bpm")}`;
            },
          },
        },
      },
      scales: {
        x: {
          type: "linear",
          title: {
            display: true,
            text: t("result.postExertion.decayXAxisLabel", "Time (s)"),
            color: colours.typography,
          },
          grid: {
            color: "rgba(147,147,147,0.2)",
          },
          ticks: {
            color: colours.typography,
          },
        },
        y: {
          title: {
            display: true,
            text: t("result.postExertion.decayYAxisLabel", "RR (bpm)"),
            color: colours.typography,
          },
          grid: {
            color: "rgba(147,147,147,0.2)",
          },
          ticks: {
            color: colours.typography,
          },
        },
      },
    }),
    [t, data.datasets.length],
  );

  return (
    <div style={{ height }}>
      <Line data={data} options={options} />
    </div>
  );
});

export default PostExertionDecayChart;
